import React from 'react';
import {
  Users,
  Wrench,
  AlertCircle,
  Sparkles,
  Package,
  Rocket,
  Calculator,
  Timer,
  Ban,
  TrendingUp,
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const WhyAstra = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.3 });
  const [coreRef, coreVisible] = useScrollAnimation({ threshold: 0.2 });
  const [eliminateRef, eliminateVisible] = useScrollAnimation({ threshold: 0.1 });

  // Astra Competitive Advantages
  const astraBenefits = [
    {
      option: 'World-Class Performance',
      icon: Sparkles,
      color: 'text-cyan-400',
      borderColor: 'border-cyan-500/30',
      bgColor: 'from-blue-500/10 to-cyan-500/10',
      highlight: true,
      painPoints: [
        { label: 'Decision Latency', value: '~2ns amortized', bad: false },
        { label: 'Throughput', value: '950M+ decisions/sec', bad: false },
        { label: 'Tick-to-Trade', value: '150-250ns (target w/ FPGA)', bad: false },
        { label: 'Quant Strategies', value: 'Avellaneda-Stoikov, OFI', bad: false },
        { label: 'Compliance', value: 'SEBI-ready architecture', bad: false },
        { label: 'Market Focus', value: 'NSE Co-location', bad: false },
      ],
    },
  ];

  // What makes ASTRA different
  const astraDelivers = [
    { icon: Users, label: 'Speed + Intelligence', description: 'Not just fast - runs real quant models' },
    { icon: Timer, label: 'Near-FPGA Performance', description: 'Target: 150-250ns with sophisticated strategies' },
    { icon: Wrench, label: 'Proprietary Technology', description: 'Built from scratch in Codon' },
    { icon: AlertCircle, label: 'Verified Benchmarks', description: 'All claims reproducible' },
    { icon: Calculator, label: 'India-First Focus', description: 'NSE, SEBI compliance built-in' },
    { icon: Ban, label: 'New Market Category', description: 'No competitor has both speed + intelligence' },
  ];

  return (
    <section id="why-astra" className="py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Package className="w-5 h-5 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Competitive Edge</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Why <span className="text-gradient">ASTRA</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            A new category that combines near-FPGA speed with
            <span className="text-white font-semibold"> sophisticated quantitative strategies</span>.
          </p>
        </div>

        {/* The Core Value Prop */}
        <div
          ref={coreRef}
          className={`max-w-4xl mx-auto mb-16 transition-all duration-1000 ${
            coreVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="p-8 rounded-2xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 border border-blue-500/20">
            <div className="text-center mb-8">
              <Rocket className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Speed + Intelligence Combined
              </h3>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Global FPGA firms are <span className="text-cyan-400 font-semibold">fast but blind</span> - limited to simple hardcoded logic.
                ASTRA runs sophisticated quant strategies at near-FPGA speeds.
              </p>
            </div>

            {/* Astra Benefits Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {astraBenefits[0].painPoints.map((point, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-background/50 border border-cyan-500/20 text-center">
                  <div className="text-xl font-bold text-green-400 mb-1">{point.value}</div>
                  <div className="text-sm text-muted-foreground">{point.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* What Astra Delivers */}
        <div
          ref={eliminateRef}
          className={`max-w-5xl mx-auto mb-16 transition-all duration-700 ${
            eliminateVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-xl font-bold text-white text-center mb-8">
            Our Competitive Advantages
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {astraDelivers.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-card/50 border border-border"
              >
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">{item.label}</h4>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="inline-block p-8 rounded-2xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 border border-blue-500/20">
            <Timer className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
            <p className="text-xl text-white font-bold mb-2">
              A New Category in <span className="text-cyan-400">HFT.</span>
            </p>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto text-sm">
              World-Class: Speed + Intelligence Combined. Targeting NSE co-location.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
              >
                Investor Inquiries
                <Rocket className="w-4 h-4" />
              </a>
              <a
                href="#faq"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border text-white font-medium rounded-xl hover:bg-white/5 transition-colors"
              >
                Technical FAQ
                <TrendingUp className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyAstra;
